import { useT } from "@magic-md/i18n/react";
import { GitBranch } from "lucide-react";
import {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/utils";
import {
    buildUnitDependencyTreesFromSource,
    canRunUnitDependencyNode,
    getPredecessorRowsForTarget,
    getSuccessorRowsForTarget,
    type UnitDependencyDisplayRow,
    type UnitDependencyKind,
    type UnitDependencyTreeNode,
} from "../../../lib/unit-dependency-tree";
import { useDocContext } from "../contexts/DocContext";

interface PopoverPosition {
    left: number;
    top: number;
    width: number;
    maxHeight: number;
    listMaxHeight: number;
}

const POPOVER_MARGIN = 8;
const POPOVER_WIDTH = 288;

function kindLabel(kind: UnitDependencyKind): string {
    switch (kind) {
        case "definition":
            return "define";
        case "for":
            return "for";
        case "content":
            return "content";
        case "import":
            return "import";
        case "use":
            return "use";
    }
}

function gotoLine(line: number) {
    window.dispatchEvent(
        new CustomEvent("magic-md:goto-line", {
            detail: { line, flash: true },
        }),
    );
}

function UnitDependencySection({
    title,
    emptyLabel,
    rows,
    canRun,
    onJump,
    runUnit,
}: {
    title: string;
    emptyLabel: string;
    rows: UnitDependencyDisplayRow[];
    canRun: (kind: UnitDependencyKind) => boolean;
    onJump: (line: number) => void;
    runUnit: (node: UnitDependencyTreeNode) => void;
}) {
    const t = useT("editor");
    return (
        <div className="mh-deps">
            <div className="mh-deps-title">
                {title}
            </div>
            {rows.length === 0 ? (
                <div className="mh-deps-empty">
                    {emptyLabel}
                </div>
            ) : (
                <div className="mh-deps-list">
                    {rows.map(({ node, depth }) => (
                        <div
                            key={`${title}:${node.unit.id}:${depth}:${node.unit.line}`}
                            className="mh-deps-row"
                            style={{ paddingLeft: `${6 + depth * 14}px` }}
                            role="menuitem"
                        >
                            <button
                                type="button"
                                className="mh-deps-jump"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onJump(node.unit.line);
                                }}
                                title={t("render.deps.jumpTo", { id: node.unit.id })}
                            >
                                <span className="mh-deps-branch" aria-hidden="true" />
                                <span className="mh-deps-label">
                                    {node.unit.id}
                                </span>
                                <span className="mh-deps-kind">
                                    {kindLabel(node.unit.kind)}
                                </span>
                                {node.repeated || node.cycle ? (
                                    <span className="mh-deps-marker">
                                        {node.cycle ? "cycle" : "seen"}
                                    </span>
                                ) : null}
                            </button>
                            {canRunUnitDependencyNode(node) && canRun(node.unit.kind) && (
                                <button
                                    type="button"
                                    className="mh-deps-run"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        runUnit(node);
                                    }}
                                    title={`Run ${node.unit.id}`}
                                    aria-label={`Run ${node.unit.id}`}
                                >
                                    ▶
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export const UnitDependencyFooterButton = memo(function UnitDependencyFooterButton({
    targetId,
    className,
}: {
    targetId: string;
    className?: string;
}) {
    const t = useT("editor");
    const doc = useDocContext();
    const [open, setOpen] = useState(false);
    const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const sourceContent = doc.getSourceContent?.() ?? doc.sourceContent;

    const dependencyTrees = useMemo(
        () =>
            sourceContent
                ? buildUnitDependencyTreesFromSource(sourceContent, targetId)
                : { predecessor: null, successor: null },
        [sourceContent, targetId],
    );

    const predecessorTree = dependencyTrees.predecessor;
    const successorTree = dependencyTrees.successor;

    const predecessorRows = useMemo(
        () => predecessorTree
            ? getPredecessorRowsForTarget(targetId, predecessorTree)
            : [],
        [targetId, predecessorTree],
    );

    const successorRows = useMemo(
        () => successorTree
            ? getSuccessorRowsForTarget(targetId, successorTree)
            : [],
        [targetId, successorTree],
    );

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event: PointerEvent) => {
            const root = rootRef.current;
            const popover = popoverRef.current;
            if (!root || !(event.target instanceof Node)) return;
            if (root.contains(event.target) || popover?.contains(event.target)) return;
            setOpen(false);
        };
        document.addEventListener("pointerdown", onPointerDown);
        return () => document.removeEventListener("pointerdown", onPointerDown);
    }, [open]);

    useLayoutEffect(() => {
        if (!open) {
            setPopoverPosition(null);
            return undefined;
        }

        const updatePosition = () => {
            const button = buttonRef.current;
            if (!button) return;

            const viewportWidth =
                window.innerWidth || document.documentElement.clientWidth || POPOVER_WIDTH;
            const viewportHeight =
                window.innerHeight || document.documentElement.clientHeight || 360;
            const rect = button.getBoundingClientRect();
            const width = Math.min(
                POPOVER_WIDTH,
                Math.max(176, viewportWidth - POPOVER_MARGIN * 2),
            );
            const maxHeight = Math.max(
                80,
                viewportHeight - POPOVER_MARGIN * 2,
            );
            const measuredHeight = Math.min(
                popoverRef.current?.offsetHeight || 320,
                maxHeight,
            );
            const maxLeft = Math.max(
                POPOVER_MARGIN,
                viewportWidth - width - POPOVER_MARGIN,
            );
            const left = Math.min(
                Math.max(rect.right - width, POPOVER_MARGIN),
                maxLeft,
            );
            const aboveTop = rect.top - measuredHeight - POPOVER_MARGIN;
            const belowTop = rect.bottom + POPOVER_MARGIN;
            const rawTop = aboveTop >= POPOVER_MARGIN ? aboveTop : belowTop;
            const maxTop = Math.max(
                POPOVER_MARGIN,
                viewportHeight - measuredHeight - POPOVER_MARGIN,
            );
            const top = Math.min(Math.max(rawTop, POPOVER_MARGIN), maxTop);
            const next = {
                left,
                top,
                width,
                maxHeight,
                listMaxHeight: Math.max(56, maxHeight - 34),
            };

            setPopoverPosition((current) => {
                if (
                    current &&
                    Math.abs(current.left - next.left) < 0.5 &&
                    Math.abs(current.top - next.top) < 0.5 &&
                    Math.abs(current.width - next.width) < 0.5 &&
                    Math.abs(current.maxHeight - next.maxHeight) < 0.5
                ) {
                    return current;
                }
                return next;
            });
        };

        updatePosition();
        const frame =
            typeof window.requestAnimationFrame === "function"
                ? window.requestAnimationFrame(updatePosition)
                : null;
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        return () => {
            if (
                frame !== null &&
                typeof window.cancelAnimationFrame === "function"
            ) {
                window.cancelAnimationFrame(frame);
            }
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [open]);

    const runUnit = useCallback(
        (node: UnitDependencyTreeNode) => {
            const id = node.unit.id;
            switch (node.unit.kind) {
                case "definition":
                    doc.onRun?.(id);
                    return;
                case "for":
                    doc.onRunFor?.(id);
                    return;
                case "content":
                    doc.onRunContent?.(id);
                    return;
                case "import":
                    doc.onRunImport?.(id);
                    return;
                case "use":
                    doc.onRunUse?.(id);
                    return;
            }
        },
        [doc],
    );

    const canRun = useCallback(
        (kind: UnitDependencyKind) => {
            if (!doc.executable) return false;
            switch (kind) {
                case "definition":
                    return !!doc.onRun;
                case "for":
                    return !!doc.onRunFor;
                case "content":
                    return !!doc.onRunContent;
                case "import":
                    return !!doc.onRunImport;
                case "use":
                    return !!doc.onRunUse;
            }
        },
        [doc],
    );

    if ((!predecessorTree && !successorTree) || !sourceContent) return null;

    const popover = (
        <div
            ref={popoverRef}
            className="magic-unit-deps-popover fixed z-50 overflow-hidden rounded-md border border-border bg-background shadow-lg"
            role="menu"
            style={{
                left: popoverPosition?.left ?? 0,
                top: popoverPosition?.top ?? 0,
                width: popoverPosition?.width ?? POPOVER_WIDTH,
                maxHeight: popoverPosition?.maxHeight ?? 320,
                visibility: popoverPosition ? "visible" : "hidden",
            }}
        >
            <div
                className="mh-deps-scroll"
                style={{
                    maxHeight: popoverPosition?.listMaxHeight ?? 286,
                    overflowY: "auto",
                }}
            >
                {predecessorTree ? (
                    <UnitDependencySection
                        title={t("render.deps.predecessors", { id: targetId })}
                        emptyLabel={t("render.deps.noPredecessors")}
                        rows={predecessorRows}
                        canRun={canRun}
                        onJump={(line) => {
                            gotoLine(line);
                            setOpen(false);
                        }}
                        runUnit={runUnit}
                    />
                ) : null}
                {successorTree ? (
                    <UnitDependencySection
                        title={t("render.deps.successors", { id: targetId })}
                        emptyLabel={t("render.deps.noSuccessors")}
                        rows={successorRows}
                        canRun={canRun}
                        onJump={(line) => {
                            gotoLine(line);
                            setOpen(false);
                        }}
                        runUnit={runUnit}
                    />
                ) : null}
            </div>
        </div>
    );

    return (
        <div ref={rootRef} className="relative">
            <button
                ref={buttonRef}
                type="button"
                className={cn(
                    "magic-render-footer-action-secondary magic-render-footer-action-dependencies flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:bg-muted",
                    className,
                )}
                onClick={(event) => {
                    event.stopPropagation();
                    setOpen((value) => !value);
                }}
                title={t("render.deps.title")}
                aria-label={t("render.deps.title")}
                aria-expanded={open}
            >
                <GitBranch size={11} aria-hidden="true" />
            </button>
            {open && typeof document !== "undefined"
                ? createPortal(popover, document.body)
                : null}
        </div>
    );
});
